import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getTransactionByMasterId, refundTransaction, updateTransactionMerchantRefIdByMasterId } from '@services/transaction.service';
import { FILTER_TRANSACTION_TYPE } from '@constants/transaction.constants';
import { CURRENCY } from '@constants/common.constants';
import { API_STATUS } from '@constants/api.constants';

export const fetchTransactionDetailByMasterId = createAsyncThunk('transaction/fetchTransactionDetailByMasterId', async ({ masterId }: { masterId: string }) => {
	return await getTransactionByMasterId({ masterId });
});

export const refundTransactionByMasterId = createAsyncThunk(
	'transaction/refundTransaction',
	async ({ masterId, amount }: { masterId: string; amount: number }) => {
		try {
			const response = await refundTransaction(masterId, amount);
			if (response?.status === API_STATUS.ERROR) {
				throw new Error(response?.code);
			}
			if (response.masterId) {
				return getTransactionByMasterId({ masterId: response.masterId });
			}
			return response;
		} catch (error: any) {
			throw new Error(error);
		}
	},
);

export const updateMerchantRefIdByMasterId = createAsyncThunk(
	'transaction/updateTransactionMerchantRefIdByMasterId',
	async ({ masterId, refId }: { masterId: string; refId: string }) => {
		try {
			const response = await updateTransactionMerchantRefIdByMasterId({ masterId, refId });
			if (response?.status === API_STATUS.ERROR) {
				throw new Error(response?.code);
			}
			return response;
		} catch (error: any) {
			throw new Error(error);
		}
	},
);

export enum TransactionType {
	PAY = 'PAY',
	PAY_CC = 'PAY_CC',
	PAY_VOID = 'PAY_VOID',
	PAY_CC_VOID = 'PAY_CC_VOID',
	EARN = 'EARN',
	EARN_VOID = 'EARN_VOID',
}

export enum TransactionGroup {
	VOID = 'VOID',
	PAY = 'PAY',
	REWARD = 'REWARD',
}

export const PaymentDetailTitles: Record<string, string> = {
	AE: 'Apple Pay',
	GO: 'Google Pay',
};

export interface PaymentDetail {
	type: TransactionType;
	tokens: number;
	paymentMethod?: string;
	amountEntered?: string;
	group?: TransactionGroup;
	remarks?: string;
	title: string;
}

export interface Privilege {
	privilageTitle: string;
	redemptionId: string;
}
export interface Voucher {
	voucherTitle: string;
	voucherId: string;
}

export interface RedeemedVoucher extends Voucher {
	purchasedVoucherId: string;
	brandName: string;
	action: string;
	redemptionDate: string;
	count?: number;
}

export interface ITransactionDetail {
	_id: string;
	createdAt: string;
	amount: number;
	merchant: string;
	currency: string | null;
	privileges?: Privilege[];
	vouchers?: Voucher[];
	group: TransactionGroup;
	transactionType: FILTER_TRANSACTION_TYPE;
	details: Partial<PaymentDetail>[];
	voidable: boolean;
	remarks: string;
	metadata?: {
		merchant: {
			refId: string;
		};
		redeemedVoucherIdsList?: RedeemedVoucher[];
	};
}

export interface IDealRedeemed {
	[key: string]: { voucherTitle: string; count: number };
}

export interface ITransactionNeededData extends Partial<ITransactionDetail> {
	rewardAmount: number;
	dealsRedeemed?: IDealRedeemed;
}

const processTransactionData = (transaction: Partial<ITransactionDetail>): ITransactionNeededData => {
	const rewardAmount = transaction.details?.find((t) => t.group === TransactionGroup.REWARD)?.tokens || 0;
	const details = transaction.details
		?.filter((item) => [TransactionGroup.VOID, TransactionGroup.PAY].includes(item.group || TransactionGroup.REWARD) && item.amountEntered)
		.map((item) => {
			let title;
			if ([TransactionType.PAY, TransactionType.PAY_VOID].includes(item.type || TransactionType.EARN)) {
				title = `KrisPay miles - ${item.tokens?.toLocaleString()}`;
			} else {
				title = PaymentDetailTitles[item.paymentMethod || ''];
			}
			return {
				...item,
				title,
			};
		});
	const transactionType = transaction.group === TransactionGroup.PAY ? FILTER_TRANSACTION_TYPE.PAYMENT : FILTER_TRANSACTION_TYPE.REFUND;

	const payTransaction = transaction.details?.find((t) => [TransactionGroup.PAY, TransactionGroup.VOID].includes(t.group || TransactionGroup.REWARD));

	// handle remarks
	let remarks = payTransaction?.remarks || '-';
	if (transactionType === FILTER_TRANSACTION_TYPE.PAYMENT) {
		const privilege = transaction.privileges?.[0];
		if (privilege) {
			remarks = `${privilege.privilageTitle} redeemed (Redemption ID: ${privilege.redemptionId})`;
		} else {
			remarks =
				details?.find((detail) => [TransactionGroup.PAY, TransactionGroup.VOID].includes(detail.group || TransactionGroup.REWARD))?.remarks || '-';
		}
	}

	const dealsRedeemed: IDealRedeemed = {};
	transaction.metadata?.redeemedVoucherIdsList?.forEach((voucher) => {
		if (!dealsRedeemed[voucher.voucherId]) {
			dealsRedeemed[voucher.voucherId] = {
				voucherTitle: voucher.voucherTitle,
				count: 1,
			};
		} else {
			dealsRedeemed[voucher.voucherId].count += 1;
		}
	});

	const result: ITransactionNeededData = {
		_id: transaction._id,
		createdAt: transaction.createdAt || '',
		amount: transaction.amount || 0,
		merchant: transaction.merchant || '',
		currency: transaction.currency || CURRENCY.SGD,
		transactionType,
		details,
		voidable: transaction.voidable,
		rewardAmount,
		remarks,
		metadata: transaction.metadata,
		dealsRedeemed,
	};

	return result;
};

export interface TransactionState {
	transaction: Partial<ITransactionNeededData>;
	isLoading: boolean;
	error: string | null;
}

const initialState = {
	transaction: {},
	isLoading: false,
	error: null,
} satisfies TransactionState as TransactionState;

const transactionDetailSlice = createSlice({
	name: 'transaction',
	initialState: initialState,
	reducers: {
		fetchTransactionDetailByMasterId: (state, action: PayloadAction<TransactionState>) => {
			return {
				...state.transaction,
				...action.payload,
			};
		},
	},
	extraReducers(builder) {
		// fetchTransactionDetailByMasterId
		builder.addCase(fetchTransactionDetailByMasterId.pending, (state: TransactionState) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addCase(fetchTransactionDetailByMasterId.fulfilled, (state: TransactionState, action) => {
			state.isLoading = false;
			state.transaction = processTransactionData(action.payload.data);
		});
		builder.addCase(fetchTransactionDetailByMasterId.rejected, (state: TransactionState) => {
			state.isLoading = false;
		});
		// refundTransactionByMasterId
		builder.addCase(refundTransactionByMasterId.pending, (state: TransactionState) => {
			state.isLoading = true;
			state.error = null;
		});
		builder.addCase(refundTransactionByMasterId.fulfilled, (state: TransactionState, action) => {
			state.isLoading = false;
			state.transaction = processTransactionData(action.payload.data);
		});
		builder.addCase(refundTransactionByMasterId.rejected, (state: TransactionState, action) => {
			state.isLoading = false;
			state.error = action?.error?.message || '';
		});
		// updateMerchantRefIdByMasterId
		builder.addCase(updateMerchantRefIdByMasterId.pending, (state: TransactionState) => {
			state.error = null;
		});
		builder.addCase(updateMerchantRefIdByMasterId.fulfilled, (state: TransactionState, action) => {
			state.transaction.metadata = {
				...state.transaction.metadata,
				merchant: {
					refId: action.meta.arg.refId,
				},
			};
			state.isLoading = false;
		});
		builder.addCase(updateMerchantRefIdByMasterId.rejected, (state: TransactionState, action) => {
			state.isLoading = false;
			state.error = action?.error?.message || '';
		});
	},
});

export const transactionDetailReducer = transactionDetailSlice.reducer;
export default transactionDetailSlice;
